import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Layout } from "../components/Layout";
import { VirtualBusinessGrid } from "../components/VirtualBusinessGrid";
import { PageRocket } from "../components/PageRocket";
import { fetchBusinessesPage, fetchCategories } from "../lib/api";

const PAGE = 30;
const SEARCH_PAGE = 24;

export default function BusinessIdeas() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSearch = searchParams.get("q") || "";
  const [query, setQuery] = useState(activeSearch);
  const [activeCat, setActiveCat] = useState("");
  const [categories, setCategories] = useState([]);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const busyRef = useRef(false);

  const isSearch = !!activeSearch;

  useEffect(() => {
    fetchCategories().then(setCategories).catch(() => {});
  }, []);

  // Infinite scroll (browse / category): append pages.
  const loadBrowse = useCallback(
    async (reset, currentLen = 0) => {
      if (busyRef.current) return;
      busyRef.current = true;
      reset ? setLoading(true) : setLoadingMore(true);
      try {
        const offset = reset ? 0 : currentLen;
        const data = await fetchBusinessesPage({ category: activeCat, limit: PAGE, offset });
        setItems((prev) => (reset ? data.items : [...prev, ...data.items]));
        setTotal(data.total);
        setHasMore(data.has_more);
      } catch {
        if (reset) setItems([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
        busyRef.current = false;
      }
    },
    [activeCat]
  );

  // Pagination (search results): replace page.
  const loadSearch = useCallback(
    async (pageNum) => {
      setLoading(true);
      try {
        const data = await fetchBusinessesPage({
          search: activeSearch,
          category: activeCat || undefined,
          limit: SEARCH_PAGE,
          offset: (pageNum - 1) * SEARCH_PAGE,
        });
        setItems(data.items);
        setTotal(data.total);
        setPage(pageNum);
      } catch {
        setItems([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [activeSearch, activeCat]
  );

  useEffect(() => {
    setQuery(activeSearch);
    setItems([]);
    if (isSearch) {
      loadSearch(1);
    } else {
      loadBrowse(true);
    }
    window.scrollTo({ top: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSearch, activeCat]);

  const submit = (e) => {
    e.preventDefault();
    setSearchParams(query.trim() ? { q: query.trim() } : {});
  };

  const clear = () => {
    setQuery("");
    setSearchParams({});
  };

  const totalPages = Math.max(1, Math.ceil(total / SEARCH_PAGE));

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-14">
        <div className="flex items-start justify-between gap-6">
          <div>
            <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-[#0F0F0F]">
              Business Ideas
            </h1>
            <p className="text-[#687280] mt-3">
              Discover ideas designed to spark your next venture. Just one more business...
            </p>
          </div>
          <PageRocket className="w-36 lg:w-52 -mt-4" />
        </div>

        <form onSubmit={submit} className="mt-8 max-w-2xl">
          <div className="flex items-center gap-3 bg-white rounded-full shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-gray-100 px-6 py-4">
            <Search size={20} className="text-[#687280] shrink-0" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search business ideas..."
              className="w-full text-base outline-none bg-transparent placeholder-[#9CA3AF] text-[#0F0F0F]"
              data-testid="ideas-search-input"
            />
            {query && (
              <button type="button" onClick={clear} className="text-[#687280] hover:text-[#0F0F0F]" data-testid="ideas-clear-btn">
                <X size={18} />
              </button>
            )}
            <button
              type="submit"
              className="shrink-0 bg-[#0F0F0F] text-white rounded-full px-6 py-2.5 text-sm font-medium hover:bg-[#D4AF37] hover:text-[#0F0F0F] transition-colors"
              data-testid="ideas-search-btn"
            >
              Search
            </button>
          </div>
        </form>

        {/* Category filter chips */}
        <div className="flex flex-wrap gap-2.5 mt-8">
          <Chip active={!activeCat} onClick={() => setActiveCat("")} label="All" testid="filter-all" />
          {categories.map((c) => (
            <Chip
              key={c.slug}
              active={activeCat === c.slug}
              onClick={() => setActiveCat(activeCat === c.slug ? "" : c.slug)}
              label={c.name}
              testid={`filter-${c.slug}`}
            />
          ))}
        </div>

        <div className="mt-10">
          {loading ? (
            <p className="text-[#687280]" data-testid="ideas-loading">Loading ideas...</p>
          ) : items.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-display text-xl font-semibold text-[#0F0F0F]">No ideas found</p>
              <p className="text-[#687280] mt-2">Try a different search or category.</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-[#687280] mb-6" data-testid="ideas-count">
                {total} {total === 1 ? "idea" : "ideas"}
                {isSearch && <span className="ml-1">for “{activeSearch}”</span>}
              </p>

              <VirtualBusinessGrid
                businesses={items}
                onEndReached={isSearch ? undefined : () => hasMore && loadBrowse(false, items.length)}
                loadingMore={loadingMore}
              />

              {isSearch && totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-12" data-testid="search-pagination">
                  <button
                    disabled={page <= 1}
                    onClick={() => loadSearch(page - 1)}
                    className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-4 py-2 text-sm font-medium disabled:opacity-40 hover:bg-[#F7F7F7] transition-colors"
                    data-testid="search-prev-btn"
                  >
                    <ChevronLeft size={16} /> Prev
                  </button>
                  <span className="text-sm text-[#687280] px-2" data-testid="search-page-indicator">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    disabled={page >= totalPages}
                    onClick={() => loadSearch(page + 1)}
                    className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-4 py-2 text-sm font-medium disabled:opacity-40 hover:bg-[#F7F7F7] transition-colors"
                    data-testid="search-next-btn"
                  >
                    Next <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </Layout>
  );
}

const Chip = ({ active, onClick, label, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
      active
        ? "bg-[#0F0F0F] text-white"
        : "bg-[#F7F7F7] text-[#687280] hover:bg-gray-200"
    }`}
  >
    {label}
  </button>
);
